import React, { useState, useEffect } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { 
  UserGroupIcon, 
  CheckmarkCircle01Icon,
  Search01Icon,
  Shield02Icon,
  Settings01Icon,
  LockKeyIcon,
  Money01Icon,
  ArrowRight01Icon,
  Clock01Icon,
  UserAdd01Icon,
  ReloadIcon,
  Copy01Icon,
  Refresh01Icon,
  Delete02Icon,
  ViewIcon,
  ViewOffIcon,
  FlashIcon
} from '@hugeicons/core-free-icons';
import Button from '../components/Button';
import { api } from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import ResponsiveModal from '../components/ResponsiveModal';
import { useNavigate } from 'react-router-dom';
import { FadeLoader } from 'react-spinners';
import { 
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

const Admin = () => {
  const { addToast } = useToast();
  const { user, login } = useAuth();
  const navigate = useNavigate();
  
  const [gatePassword, setGatePassword] = useState('');
  const [isGateUnlocked, setIsGateUnlocked] = useState(() => {
    return sessionStorage.getItem('admin_gate_unlocked') === 'true';
  });
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshInterval, setRefreshInterval] = useState(() => {
    return parseInt(localStorage.getItem('admin_refresh_interval')) || 10000;
  });
  const [countdown, setCountdown] = useState(refreshInterval / 1000);
  
  const [email, setEmail] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('metrics'); 
  
  const [metrics, setMetrics] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [users, setUsers] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [txPagination, setTxPagination] = useState({ current_page: 1, last_page: 1, total: 0 });
  const [verifications, setVerifications] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [txSearch, setTxSearch] = useState('');
  const [newAdminPass, setNewAdminPass] = useState('');
  
  const [adminAccount, setAdminAccount] = useState({ deriv_cr_number: '', verification_fee: '' });
  const [displayAccount, setDisplayAccount] = useState({ deriv_cr_number: '', verification_fee: '' });
  const [isUpdatingAccount, setIsUpdatingAccount] = useState(false);
  
  const [metricDetail, setMetricDetail] = useState(null); 
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [selectedVerif, setSelectedVerif] = useState(null);
  const [refundTxId, setRefundTxId] = useState('');
  const [refundOtp, setRefundOtp] = useState('');
  const [showRefundOtpInput, setShowRefundOtpInput] = useState(false);
  const [isAdminInviteOpen, setIsAdminInviteOpen] = useState(false);
  const [adminEmail, setAdminInviteEmail] = useState('');

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/login', { login: email, password: loginPass, remember_me: true });
      const userData = res.user || res.data?.user;
      const token = res.access_token || res.data?.access_token;
      if (userData && token) {
        login(userData, token, true);
        addToast("Authenticated. Please unlock terminal.", "success");
      } else { throw new Error("Invalid response format"); }
    } catch (err) { addToast(err.message || "Login Failed", "error"); } finally { setLoading(false); }
  };

  const handleGateUnlock = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/admin/verify-password', { password: gatePassword });
      setIsGateUnlocked(true);
      sessionStorage.setItem('admin_gate_unlocked', 'true');
      addToast("Command Center Unlocked", "success");
    } catch (err) { addToast(err.message || "Invalid Gate Password", "error"); } finally { setLoading(false); }
  };

  const fetchData = async (background = false, page = 1) => {
    if (!background) setIsInitialLoading(true);
    try {
      if (activeTab === 'metrics') {
        const [mRes, cRes] = await Promise.all([api.get('/admin/metrics'), api.get('/admin/chart-data')]);
        setMetrics(mRes.data || {});
        setChartData(cRes.data || []);
      } else if (activeTab === 'users') {
        const res = await api.get(`/admin/users?search=${userSearch}`);
        setUsers(res.data.data || []);
      } else if (activeTab === 'transactions') {
        const res = await api.get(`/admin/transactions?search=${txSearch}&page=${page}`);
        setTransactions(res.data.data || []);
        setTxPagination({
          current_page: res.data.current_page,
          last_page: res.data.last_page,
          total: res.data.total
        });
      } else if (activeTab === 'verifications') {
        const res = await api.get('/admin/verifications');
        setVerifications(res.data?.data || res.data || []);
      } else if (activeTab === 'settings') {
        const [aRes, sRes] = await Promise.all([
          api.get('/admin/users?role=admin'),
          api.get('/admin/account')
        ]);
        setAdmins(aRes.data?.data || []);
        if (sRes.data) {
          const data = { deriv_cr_number: sRes.data.deriv_cr_number, verification_fee: sRes.data.verification_fee };
          setDisplayAccount(data);
          if (!adminAccount.deriv_cr_number) setAdminAccount(data);
        }
      }
    } catch (err) { console.error("Fetch error:", err); } 
    finally {
      setIsInitialLoading(false);
      setCountdown(refreshInterval / 1000);
    }
  };

  useEffect(() => {
    if (isGateUnlocked) fetchData(false);
  }, [isGateUnlocked, activeTab]);

  useEffect(() => {
    if (isGateUnlocked) {
      const timer = setInterval(() => setCountdown(prev => Math.max(0, prev - 1)), 1000);
      const fetcher = setInterval(() => fetchData(true), refreshInterval);
      return () => { clearInterval(timer); clearInterval(fetcher); };
    }
  }, [isGateUnlocked, refreshInterval, userSearch, txSearch]);

  useEffect(() => {
    if (isGateUnlocked && (userSearch || txSearch)) {
      const handler = setTimeout(() => fetchData(true), 500);
      return () => clearTimeout(handler);
    }
  }, [userSearch, txSearch]);

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    await fetchData(true);
    setTimeout(() => setIsRefreshing(false), 800);
    addToast("Data synchronized", "success");
  };

  const handleRequestRefundOTP = async () => {
    setLoading(true);
    try {
      await api.post(`/admin/verifications/${selectedVerif.uid}/request-otp`);
      addToast("Refund OTP sent to your admin email.", "success");
      setShowRefundOtpInput(true);
    } catch (err) {
      addToast(err.message || "Failed to request OTP", "error");
    } finally {
      setLoading(false);
    }
  };

  const updateRefreshInterval = (ms) => {
    setRefreshInterval(ms);
    setCountdown(ms / 1000);
    localStorage.setItem('admin_refresh_interval', ms);
    addToast(`Refresh rate set to ${ms/1000}s`, "success");
  };

  const handleProcessRefund = async (isAutomated = false) => {
    if (!isAutomated && !refundTxId) return addToast("Enter Refund TX ID", "error");
    if (isAutomated && !refundOtp) return addToast("Enter Verification OTP", "error");
    
    setLoading(true);
    try {
      await api.post(`/admin/verifications/${selectedVerif.uid}/refund`, { 
        refund_transaction_id: refundTxId,
        verification_code: refundOtp,
        automated: isAutomated
      });
      addToast(isAutomated ? "Automated Refund Successful!" : "Agent verified and refunded!", "success");
      setIsRefundModalOpen(false);
      setRefundTxId('');
      setRefundOtp('');
      setShowRefundOtpInput(false);
      fetchData(true);
    } catch (err) { addToast(err.message, "error"); } finally { setLoading(false); }
  };

  const handleInviteAdmin = async () => {
    try {
      await api.post('/admin/add-admin', { email: adminEmail });
      addToast("Admin role assigned successfully", "success");
      setIsAdminInviteOpen(false);
      setAdminInviteEmail('');
      fetchData(true);
    } catch (err) { addToast(err.message, "error"); }
  };

  const handleRevokeAdmin = async (email) => {
    if (!window.confirm(`Revoke admin access for ${email}?`)) return;
    try {
      await api.post('/admin/revoke-access', { email });
      addToast("Admin access revoked", "success");
      fetchData(true);
    } catch (err) { addToast(err.message, "error"); }
  };

  const handleUpdatePassword = async () => {
    if (newAdminPass.length < 4) return addToast("Min 4 characters", "error");
    try {
      await api.post('/admin/change-password', { password: newAdminPass });
      addToast("Admin password updated!", "success");
      setNewAdminPass('');
    } catch (err) { addToast(err.message, "error"); }
  };

  const handleUpdateAccount = async () => {
    setIsUpdatingAccount(true);
    try {
      await api.post('/admin/update-payment-account', adminAccount);
      addToast("Admin payment details updated!", "success");
      fetchData(true);
    } catch (err) { addToast(err.message, "error"); }
    finally { setIsUpdatingAccount(false); }
  };

  const [debugUserId, setDebugUserId] = useState('');
  const [debugPassword, setDebugPassword] = useState('');
  const [isDebugLoading, setIsDebugLoading] = useState(false);
  const [unlinkUserId, setUnlinkUserId] = useState('');
  const [isUnlinkingGeneral, setIsUnlinkingGeneral] = useState(false);
  const [userLookupSearch, setUserLookupSearch] = useState('');
  const [lookupResults, setLookupResults] = useState([]);
  const [isLookupLoading, setIsLookupLoading] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const [isNavbarVisible, setIsNavbarVisible] = useState(true);
  const [isNavbarEnabled, setIsNavbarEnabled] = useState(true);
  const [forceTxUuid, setForceTxUuid] = useState('');
  const [isForceLoading, setIsForceLoading] = useState(false);

  const handleForceComplete = async () => {
    if (!forceTxUuid) return addToast("Enter Transaction UID", "error");
    if (!window.confirm("Force complete this transaction? This bypasses Deriv and triggers rewards.")) return;
    setIsForceLoading(true);
    try {
      await api.post('/admin/transactions/force-complete', { transaction_uuid: forceTxUuid });
      addToast("Transaction forced complete!", "success");
      setForceTxUuid('');
    } catch (err) {
      addToast(err.message || "Failed to force complete", "error");
    } finally {
      setIsForceLoading(false);
    }
  };

  const handleTxPageChange = (page) => {
    fetchData(false, page);
  };

  // Inactivity timer for mobile navbar
  useEffect(() => {
    let timeoutId;
    const handleActivity = () => {
      setIsNavbarVisible(true);
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setIsNavbarVisible(false);
      }, 3000); // Hide after 3 seconds of inactivity
    };

    if (isNavbarEnabled) {
      window.addEventListener('scroll', handleActivity);
      window.addEventListener('mousemove', handleActivity);
      window.addEventListener('mousedown', handleActivity);
      window.addEventListener('touchstart', handleActivity);
      window.addEventListener('keydown', handleActivity);
      
      handleActivity(); // Initial show
    }

    return () => {
      window.removeEventListener('scroll', handleActivity);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('mousedown', handleActivity);
      window.removeEventListener('touchstart', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      clearTimeout(timeoutId);
    };
  }, [isNavbarEnabled]);

  const handleCheckPassword = async () => {
    if (!debugUserId || !debugPassword) return addToast("Enter User UID and Password", "error");
    setIsDebugLoading(true);
    try {
      const res = await api.post('/admin/check-user-password', { 
        user_id: debugUserId, 
        password: debugPassword 
      });
      if (res.data?.matches) {
        addToast(`MATCH! Verified for ${res.data.user_email}`, "success");
      } else {
        addToast(`NO MATCH for ${res.data?.user_email || 'user'}`, "error");
      }
    } catch (err) { 
      addToast(err.message || "User not found or error occurred", "error"); 
    } finally { 
      setIsDebugLoading(false); 
    }
  };

  const handleUnlinkUniversal = async () => {
    if (!unlinkUserId) return addToast("Enter User UID to unlink", "error");
    if (!window.confirm(`Unlink all Deriv connections for user ${unlinkUserId}?`)) return;
    
    setIsUnlinkingGeneral(true);
    try {
      const res = await api.post(`/admin/users/${unlinkUserId}/unlink-deriv`);
      addToast(res.message || "Successfully unlinked accounts", "success");
      setUnlinkUserId('');
    } catch (err) {
      addToast(err.message || "Failed to unlink account", "error");
    } finally {
      setIsUnlinkingGeneral(false);
    }
    };

    const handleUserLookup = async (e) => {
    e.preventDefault();
    if (!userLookupSearch || userLookupSearch.length < 3) return addToast("Enter at least 3 characters", "error");

    setIsLookupLoading(true);
    try {
      const res = await api.get(`/admin/users?search=${userLookupSearch}`);
      setLookupResults(res.data.data || []);
      if ((res.data.data || []).length === 0) addToast("No users found", "info");
    } catch (err) {
      addToast(err.message || "Lookup failed", "error");
    } finally {
      setIsLookupLoading(false);
    }
    };

    // --- RENDER STATES ---
  if (!user || typeof user === 'string') {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 text-white">
        <form onSubmit={handleAdminLogin} className="max-w-md w-full bg-[#111] p-10 rounded-[3rem] border border-white/10 shadow-2xl text-center space-y-8 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-soft-green to-transparent opacity-50"></div>
          <div className="bg-white/5 p-6 rounded-full inline-block"><HugeiconsIcon icon={LockKeyIcon} size={48} className="text-gray-400" /></div>
          <div><h1 className="text-3xl font-black tracking-tight mb-2">System Access</h1><p className="text-gray-500 text-sm">Authorized Personnel Only.</p></div>
          <div className="space-y-4">
            <input type="email" placeholder="Admin Email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-6 py-4 bg-black/50 border border-white/10 rounded-2xl text-center outline-none focus:border-soft-green/50 text-white" />
            <input type="password" placeholder="Password" value={loginPass} onChange={(e) => setLoginPass(e.target.value)} className="w-full px-6 py-4 bg-black/50 border border-white/10 rounded-2xl text-center outline-none focus:border-soft-green/50 text-white" />
          </div>
          <Button type="submit" disabled={loading} className="w-full py-4 rounded-2xl font-black bg-white text-black border-none">{loading ? 'Verifying...' : 'Identify'}</Button>
        </form>
      </div>
    );
  }

  const isAdmin = user.roles?.some(r => r.name === 'admin') || user.role === 'admin' || user.role?.name === 'admin';
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex flex-col items-center justify-center text-red-500 space-y-6 px-4 text-center">
        <HugeiconsIcon icon={Shield02Icon} size={64} /><h1 className="text-3xl font-black uppercase tracking-widest">Access Denied</h1><p className="text-gray-500">Your account <b>({user.email})</b> is not an admin.</p>
        <Button onClick={() => { localStorage.clear(); sessionStorage.clear(); window.location.reload(); }} variant="secondary">Return to Admin Login</Button>
      </div>
    );
  }

  if (!isGateUnlocked) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 text-white">
        <form onSubmit={handleGateUnlock} className="max-w-md w-full bg-[#111] p-10 rounded-[3rem] border border-white/10 shadow-2xl text-center space-y-8">
          <div className="bg-soft-green/10 p-6 rounded-full inline-block"><HugeiconsIcon icon={Shield02Icon} size={48} className="text-soft-green" /></div>
          <h1 className="text-3xl font-black tracking-tight">Security Check</h1>
          <input type="password" placeholder="Passcode" value={gatePassword} onChange={(e) => setGatePassword(e.target.value)} className="w-full px-6 py-4 bg-black/50 border border-white/10 rounded-2xl text-center text-2xl font-mono tracking-widest outline-none focus:border-soft-green/50 text-white" />
          <Button type="submit" disabled={loading} className="w-full py-4 rounded-2xl font-black bg-soft-green text-black border-none">{loading ? 'Decrypting...' : 'Unlock Terminal'}</Button>
        </form>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-[#F5F7FA] dark:bg-[#0A0A0A] p-4 sm:p-8 text-charcoal dark:text-white">
        <div className="max-w-7xl mx-auto space-y-8">
          
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div><h1 className="text-4xl font-black tracking-tight">Safzi Admin Page</h1><p className="text-muted-gray mt-1 font-medium text-sm">Real-time platform oversight.</p></div>
            <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 w-full sm:w-auto">
              <button onClick={handleManualRefresh} disabled={isRefreshing} className="group flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-100 dark:border-white/10 rounded-xl hover:bg-gray-50 transition-all shadow-sm font-black text-xs text-muted-gray hover:text-black dark:hover:text-white min-w-[80px] justify-center w-full sm:w-auto" title="Sync Data">
                <HugeiconsIcon icon={ReloadIcon} size={16} className={`${isRefreshing ? 'animate-spin' : ''}`} />
                <span>{countdown}s</span>
              </button>
              <button 
                onClick={() => setIsNavbarEnabled(!isNavbarEnabled)} 
                className={`sm:hidden flex items-center justify-center p-2.5 rounded-xl border transition-all shadow-sm w-full ${isNavbarEnabled ? 'bg-soft-green text-black border-soft-green' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 border-gray-100 dark:border-white/10'}`}
                title={isNavbarEnabled ? 'Hide Menu' : 'Show Menu'}
              >
                <HugeiconsIcon icon={isNavbarEnabled ? ViewIcon : ViewOffIcon} size={18} />
                <span className="ml-2 text-[10px] font-black uppercase">{isNavbarEnabled ? 'Hide' : 'Show'} Nav</span>
              </button>
              <Button onClick={() => setIsAdminInviteOpen(true)} variant="secondary" className="px-4 py-2.5 rounded-xl font-black text-xs flex items-center justify-center gap-2 shadow-sm w-full sm:w-auto"><HugeiconsIcon icon={UserAdd01Icon} size={16} /><span>Add Admin</span></Button>
              <Button onClick={() => { setIsGateUnlocked(false); sessionStorage.removeItem('admin_gate_unlocked'); }} variant="danger" className="px-4 py-2.5 rounded-xl font-black text-xs border-none bg-red-50 dark:bg-red-900/20 text-red-500 flex items-center justify-center gap-2 shadow-sm w-full sm:w-auto"><HugeiconsIcon icon={LockKeyIcon} size={16} /><span>Lock Terminal</span></Button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            {/* Desktop Tabs - Hidden on Mobile */}
            <div className="hidden sm:flex gap-2 bg-white dark:bg-gray-800 p-1.5 rounded-2xl w-fit border border-gray-100 dark:border-white/10 overflow-x-auto no-scrollbar">
              {['metrics', 'users', 'transactions', 'verifications', 'tools', 'settings'].map(tab => (
                <button key={tab} onClick={() => setActiveTab(tab)} className={`px-6 py-2.5 rounded-xl text-sm font-black capitalize transition-all whitespace-nowrap ${activeTab === tab ? 'bg-black text-white dark:bg-white dark:text-black shadow-lg' : 'text-gray-500 hover:text-black dark:hover:text-white'}`}>{tab}</button>
              ))}
            </div>

            {/* Mobile Floating Navbar - Always Dark */}
            <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex sm:hidden items-center gap-1 bg-[#111]/95 backdrop-blur-xl p-2 rounded-[2rem] border border-white/10 shadow-2xl min-w-[280px] justify-around transition-all duration-500 ease-in-out ${(!isNavbarVisible || !isNavbarEnabled) ? 'translate-y-32 opacity-0 pointer-events-none' : 'translate-y-0 opacity-100'}`}>
              <button 
                onClick={() => setActiveTab('users')} 
                className={`flex flex-col items-center gap-1 px-4 py-2 rounded-2xl transition-all ${activeTab === 'users' ? 'text-soft-green scale-110' : 'text-gray-400'}`}
              >
                <HugeiconsIcon icon={UserGroupIcon} size={20} />
                <span className="text-[8px] font-black uppercase">Users</span>
              </button>
              <button 
                onClick={() => setActiveTab('transactions')} 
                className={`flex flex-col items-center gap-1 px-4 py-2 rounded-2xl transition-all ${activeTab === 'transactions' ? 'text-soft-green scale-110' : 'text-gray-400'}`}
              >
                <HugeiconsIcon icon={Money01Icon} size={20} />
                <span className="text-[8px] font-black uppercase">Txs</span>
              </button>
              <button 
                onClick={() => setIsMoreMenuOpen(true)} 
                className={`flex flex-col items-center gap-1 px-4 py-2 rounded-2xl transition-all ${['verifications', 'tools', 'settings'].includes(activeTab) ? 'text-soft-green scale-110' : 'text-gray-400'}`}
              >
                <HugeiconsIcon icon={Settings01Icon} size={20} />
                <span className="text-[8px] font-black uppercase">More</span>
              </button>
            </div>

            {(activeTab === 'users' || activeTab === 'transactions') && (
              <div className="relative w-full sm:max-w-xs animate-in fade-in slide-in-from-right-4 duration-300">
                <HugeiconsIcon icon={Search01Icon} size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-gray" />
                <input type="text" placeholder={`Search ${activeTab}...`} value={activeTab === 'users' ? userSearch : txSearch} onChange={(e) => activeTab === 'users' ? setUserSearch(e.target.value) : setTxSearch(e.target.value)} className="w-full pl-11 pr-4 py-3 bg-white dark:bg-gray-800 border border-gray-100 dark:border-white/10 rounded-2xl text-sm text-black dark:text-white focus:ring-2 focus:ring-soft-green transition-all shadow-sm" />
              </div>
            )}
          </div>

          <div className="bg-white/80 dark:bg-[#111] backdrop-blur-md rounded-[2.5rem] border border-gray-100 dark:border-white/10 overflow-hidden shadow-sm min-h-[60vh] flex flex-col">
            {isInitialLoading ? (
              <div className="flex-1 flex flex-col items-center justify-center space-y-4 py-20"><FadeLoader color="#4ade80" /><p className="text-[10px] font-black uppercase tracking-widest text-muted-gray animate-pulse">Syncing Command Data...</p></div>
            ) : (
              <div className="animate-in fade-in duration-500">
                {activeTab === 'metrics' && metrics && (
                  <div className="p-8 space-y-8">
                    <div>
                      <p className="text-[10px] font-black uppercase text-muted-gray tracking-[0.2em] mb-4 opacity-60">Click a card below to see detailed insights</p>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div onClick={() => setMetricDetail('users')} className="bg-blue-500/10 p-8 rounded-[2rem] border border-blue-500/20 cursor-pointer hover:scale-[1.02] transition-transform">
                          <div className="flex items-center justify-between mb-4"><div className="p-3 bg-blue-500/20 rounded-full text-blue-500"><HugeiconsIcon icon={UserGroupIcon} size={24} /></div><span className="text-[10px] font-black uppercase text-blue-500 tracking-[0.2em]">User Base</span></div>
                          <p className="text-4xl font-black text-black dark:text-white">{metrics.users.total}</p><p className="text-xs text-muted-gray mt-1">Clients & Agents</p>
                        </div>
                        <div onClick={() => setMetricDetail('transactions')} className="bg-soft-green/10 p-8 rounded-[2rem] border border-soft-green/20 cursor-pointer hover:scale-[1.02] transition-transform">
                          <div className="flex items-center justify-between mb-4"><div className="p-3 bg-soft-green/20 rounded-full text-soft-green"><HugeiconsIcon icon={CheckmarkCircle01Icon} size={24} /></div><span className="text-[10px] font-black uppercase text-soft-green tracking-[0.2em]">Transactions</span></div>
                          <p className="text-4xl font-black text-black dark:text-white">{metrics.transactions.total}</p><p className="text-xs text-muted-gray mt-1">{metrics.transactions.success_rate}% Success Rate</p>
                        </div>
                        <div onClick={() => setMetricDetail('volume')} className="bg-purple-500/10 p-8 rounded-[2rem] border border-purple-500/20 cursor-pointer hover:scale-[1.02] transition-transform">
                          <div className="flex items-center justify-between mb-4"><div className="p-3 bg-purple-500/20 rounded-full text-purple-500"><HugeiconsIcon icon={Money01Icon} size={24} /></div><span className="text-[10px] font-black uppercase text-purple-500 tracking-[0.2em]">Volume</span></div>
                          <p className="text-4xl font-black text-black dark:text-white">${parseFloat(metrics.volume.usd).toLocaleString()}</p><p className="text-xs text-muted-gray mt-1">Processed in USD</p>
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="bg-white dark:bg-black/20 p-8 rounded-[2.5rem] border border-gray-100 dark:border-white/5 h-[400px] flex flex-col overflow-hidden text-black dark:text-white">
                        <h3 className="font-black text-sm uppercase tracking-widest mb-6">Traffic Trend</h3>
                        <div className="flex-1 w-full min-h-0"><ResponsiveContainer width="99%" height="100%"><AreaChart data={chartData} margin={{ left: -20, right: 10 }}><defs><linearGradient id="colorTx" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#2F855A" stopOpacity={0.3}/><stop offset="95%" stopColor="#2F855A" stopOpacity={0}/></linearGradient></defs><CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB33" /><XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 10, fontWeight: 700, fill: '#9CA3AF'}} dy={10} /><YAxis axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#9CA3AF'}} /><Tooltip contentStyle={{borderRadius: '16px', border: 'none', background: '#111', color: '#fff'}} /><Area type="monotone" dataKey="transactions" stroke="#2F855A" strokeWidth={3} fillOpacity={1} fill="url(#colorTx)" /></AreaChart></ResponsiveContainer></div>
                      </div>
                      <div className="bg-white dark:bg-black/20 p-8 rounded-[2.5rem] border border-gray-100 dark:border-white/5 h-[400px] flex flex-col overflow-hidden text-black dark:text-white">
                        <h3 className="font-black text-sm uppercase tracking-widest mb-6">User Growth</h3>
                        <div className="flex-1 w-full min-h-0"><ResponsiveContainer width="99%" height="100%"><BarChart data={chartData} margin={{ left: -20, right: 10 }}><CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB33" /><XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 10, fontWeight: 700, fill: '#9CA3AF'}} dy={10} /><YAxis axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#9CA3AF'}} /><Tooltip cursor={{fill: '#F3F4F633'}} contentStyle={{borderRadius: '16px', border: 'none', background: '#111', color: '#fff'}} /><Bar dataKey="users" fill="#3B82F6" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'users' && (
                  users.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead className="bg-gray-50 dark:bg-white/5 text-gray-400 text-[10px] uppercase font-black tracking-[0.2em]">
                          <tr className="border-b border-gray-100 dark:border-white/10">
                            <th className="px-8 py-5">User</th>
                            <th className="px-8 py-5">UID (Click to copy)</th>
                            <th className="px-8 py-5">Role</th>
                            <th className="px-8 py-5 text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                          {users.map(u => (
                            <tr key={u.uid} className="hover:bg-soft-green/5 transition-colors cursor-pointer text-black dark:text-white">
                              <td onClick={() => navigate(`/admin/users/${u.uid || u.id}`)} className="px-8 py-6">
                                <p className="font-bold">{u.business_name || u.name || 'N/A'}</p>
                                <p className="text-xs text-gray-500">{u.email}</p>
                              </td>
                              <td className="px-8 py-6">
                                <button onClick={(e) => { e.stopPropagation(); navigator.clipboard.writeText(u.uid); addToast("UID Copied!", "success"); }} className="font-mono text-[10px] bg-gray-100 dark:bg-black/40 px-3 py-1.5 rounded-lg border border-transparent hover:border-soft-green/50 transition-all">{u.uid}</button>
                              </td>
                              <td onClick={() => navigate(`/admin/users/${u.uid || u.id}`)} className="px-8 py-6 uppercase font-black text-[10px] text-gray-500">{u.roles?.map(r => r.name).join(', ') || 'Client'}</td>
                              <td onClick={() => navigate(`/admin/users/${u.uid || u.id}`)} className="px-8 py-6 text-right"><HugeiconsIcon icon={ArrowRight01Icon} size={18} className="text-gray-300 ml-auto" /></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-32 flex flex-col items-center justify-center text-center px-6">
                      <div className="p-6 bg-gray-50 dark:bg-white/5 rounded-full mb-4 text-muted-gray opacity-20"><HugeiconsIcon icon={UserGroupIcon} size={48} /></div>
                      <p className="text-muted-gray font-black uppercase text-[10px] tracking-[0.2em] opacity-40">No Users Found</p>
                    </div>
                  )
                )}

                {activeTab === 'transactions' && (
                  transactions.length > 0 ? (
                    <div className="flex flex-col h-full">
                      <div className="overflow-x-auto">
                        <table className="w-full text-left">
                          <thead className="bg-gray-50 dark:bg-white/5 text-gray-400 text-[10px] uppercase font-black tracking-[0.2em]">
                            <tr className="border-b border-gray-100 dark:border-white/10">
                              <th className="px-8 py-5">Tx UID (Copy)</th>
                              <th className="px-8 py-5">Ref</th>
                              <th className="px-8 py-5">Order Type</th>
                              <th className="px-8 py-5 text-right">Amount</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                            {transactions.map(tx => (
                              <tr key={tx.uid || tx.id} className="hover:bg-soft-green/5 transition-colors text-black dark:text-white font-bold">
                                <td className="px-8 py-6">
                                  <button 
                                    onClick={(e) => { 
                                      e.stopPropagation(); 
                                      navigator.clipboard.writeText(tx.uid); 
                                      addToast("UID Copied!", "success"); 
                                    }} 
                                    className="font-mono text-[9px] bg-gray-100 dark:bg-black/40 px-2 py-1 rounded-lg border border-transparent hover:border-soft-green/50 transition-all"
                                  >
                                    {tx.uid ? `${tx.uid.substring(0, 8)}...` : 'N/A'}
                                  </button>
                                </td>
                                <td onClick={() => navigate(`/admin/orders/${tx.uid || tx.id}`)} className="px-8 py-6 uppercase font-mono text-xs cursor-pointer">{tx.reference}</td>
                                <td className="px-8 py-6 uppercase font-black text-[10px]">
                                  <span className={`px-2 py-1 rounded ${tx.order?.transaction_type === 'deposit' ? 'bg-blue-500/10 text-blue-500' : 'bg-purple-500/10 text-purple-500'}`}>
                                    {tx.order?.transaction_type || 'N/A'}
                                  </span>
                                </td>
                                <td className="px-8 py-6 text-right font-black">₦{parseFloat(tx.amount_sent).toLocaleString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      {txPagination.last_page > 1 && (
                        <div className="p-6 border-t border-gray-100 dark:border-white/10 flex items-center justify-between">
                          <p className="text-[10px] font-black uppercase text-muted-gray">Total: {txPagination.total} Transactions</p>
                          <div className="flex gap-2">
                            <button 
                              disabled={txPagination.current_page === 1}
                              onClick={() => handleTxPageChange(txPagination.current_page - 1)}
                              className="px-4 py-2 bg-gray-100 dark:bg-white/5 rounded-xl text-[10px] font-black uppercase disabled:opacity-30"
                            >
                              Prev
                            </button>
                            <span className="px-4 py-2 text-[10px] font-black">{txPagination.current_page} / {txPagination.last_page}</span>
                            <button 
                              disabled={txPagination.current_page === txPagination.last_page}
                              onClick={() => handleTxPageChange(txPagination.current_page + 1)}
                              className="px-4 py-2 bg-gray-100 dark:bg-white/5 rounded-xl text-[10px] font-black uppercase disabled:opacity-30"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="py-32 flex flex-col items-center justify-center text-center px-6">
                      <div className="p-6 bg-gray-50 dark:bg-white/5 rounded-full mb-4 text-muted-gray opacity-20"><HugeiconsIcon icon={Money01Icon} size={48} /></div>
                      <p className="text-muted-gray font-black uppercase text-[10px] tracking-[0.2em] opacity-40">No Transactions Recorded</p>
                    </div>
                  )
                )}

                {activeTab === 'verifications' && (
                  verifications.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead className="bg-gray-50 dark:bg-white/5 text-gray-400 text-[10px] uppercase font-black tracking-[0.2em]">
                          <tr className="border-b border-gray-100 dark:border-white/10">
                            <th className="px-8 py-5">Agent</th>
                            <th className="px-8 py-5">Deriv CR</th>
                            <th className="px-8 py-5 text-center">Proof</th>
                            <th className="px-8 py-5 text-right">Action</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-white/10">
                          {verifications.map(v => (
                            <tr key={v.uid} className="hover:bg-soft-green/5 transition-colors text-black dark:text-white">
                              <td className="px-8 py-6 font-bold">{v.user?.email}</td>
                              <td className="px-8 py-6 font-mono text-sm">{v.user?.deriv_accounts?.[0]?.deriv_id || 'N/A'}</td>
                              <td className="px-8 py-6 text-center">{v.receipt_url ? (<a href={v.receipt_url} target="_blank" rel="noopener noreferrer" className="text-[10px] font-black uppercase text-blue-500 hover:underline">View Receipt</a>) : <span className="text-[10px] text-gray-400 font-bold uppercase">No Upload</span>}</td>
                              <td className="px-8 py-6 text-right">{v.status === 'pending_verification' ? <button onClick={() => { setSelectedVerif(v); setIsRefundModalOpen(true); }} className="bg-black text-white dark:bg-white dark:text-black px-4 py-2 rounded-xl text-xs font-black">Verify & Refund</button> : <span className="text-xs font-black uppercase text-soft-green">Verified</span>}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="py-32 flex flex-col items-center justify-center text-center px-6">
                      <div className="p-6 bg-gray-50 dark:bg-white/5 rounded-full mb-4 text-muted-gray opacity-20"><HugeiconsIcon icon={Shield02Icon} size={48} /></div>
                      <p className="text-muted-gray font-black uppercase text-[10px] tracking-[0.2em] opacity-40">No Pending Verifications</p>
                    </div>
                  )
                )}

                {activeTab === 'tools' && (
                  <div className="p-4 sm:p-10 max-w-4xl mx-auto space-y-8 sm:space-y-12">
                    <div className="text-center">
                      <div className="bg-purple-500/10 p-6 rounded-full inline-block mb-4"><HugeiconsIcon icon={Settings01Icon} size={48} className="text-purple-500" /></div>
                      <h2 className="text-2xl sm:text-3xl font-black">Admin Toolkit</h2>
                      <p className="text-gray-500 text-sm mt-2">Diagnostic and manual override tools.</p>
                    </div>

                    {/* User Identity Lookup */}
                    <div className="bg-white dark:bg-white/5 p-6 sm:p-8 rounded-[2rem] sm:rounded-[2.5rem] border border-gray-100 dark:border-white/10 space-y-6">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-green-500/10 rounded-xl text-green-500"><HugeiconsIcon icon={UserGroupIcon} size={20} /></div>
                        <h3 className="font-black text-sm uppercase tracking-tight">User Identity Lookup</h3>
                      </div>
                      <p className="text-[10px] text-muted-gray leading-relaxed uppercase font-bold">Search for a user by email or name to retrieve their UID.</p>
                      
                      <form onSubmit={handleUserLookup} className="flex flex-col sm:flex-row gap-3">
                        <input 
                          type="text" 
                          placeholder="Search Email or Name..." 
                          value={userLookupSearch} 
                          onChange={(e) => setUserLookupSearch(e.target.value)} 
                          className="flex-1 px-5 py-3.5 bg-gray-50 dark:bg-black/50 rounded-xl font-bold text-sm outline-none border border-gray-100 dark:border-white/10 text-black dark:text-white w-full" 
                        />
                        <Button type="submit" disabled={isLookupLoading} className="w-full sm:w-auto px-8 py-3.5 rounded-xl font-black bg-black text-white dark:bg-white dark:text-black text-xs">
                          {isLookupLoading ? '...' : 'Search'}
                        </Button>
                      </form>

                      {lookupResults.length > 0 && (
                        <div className="space-y-3 mt-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                          {lookupResults.map(u => (
                            <div key={u.uid} className="p-4 bg-gray-50 dark:bg-black/30 rounded-2xl flex flex-col sm:flex-row sm:items-center justify-between gap-4 group border border-transparent hover:border-soft-green/30 transition-all">
                              <div className="min-w-0">
                                <p className="font-bold text-xs truncate">{u.business_name || u.name || 'Anonymous'}</p>
                                <p className="text-[10px] text-muted-gray truncate">{u.email}</p>
                              </div>
                              <div className="flex items-center justify-between sm:justify-end gap-3 bg-white/50 dark:bg-black/20 p-2 sm:p-0 rounded-xl sm:bg-transparent">
                                <code className="bg-white dark:bg-black/50 px-3 py-1.5 rounded-lg text-[9px] sm:text-[10px] font-mono border border-gray-100 dark:border-white/5 truncate max-w-[150px] sm:max-w-none">{u.uid}</code>
                                <button 
                                  onClick={() => { 
                                    navigator.clipboard.writeText(u.uid); 
                                    addToast("UID Copied!", "success"); 
                                  }}
                                  className="p-2.5 bg-soft-green/10 text-soft-green rounded-lg hover:scale-110 transition-transform flex-shrink-0"
                                  title="Copy UID"
                                >
                                  <HugeiconsIcon icon={Copy01Icon} size={16} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                      {/* Password Match Debugger */}
                      <div className="bg-white dark:bg-white/5 p-6 sm:p-8 rounded-[2rem] sm:rounded-[2.5rem] border border-gray-100 dark:border-white/10 space-y-6 flex flex-col">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-blue-500/10 rounded-xl text-blue-500"><HugeiconsIcon icon={LockKeyIcon} size={20} /></div>
                          <h3 className="font-black text-sm uppercase tracking-tight">Password Debugger</h3>
                        </div>
                        <p className="text-[10px] text-muted-gray leading-relaxed uppercase font-bold flex-1">Check if a plain text password matches a user's stored hash.</p>
                        <div className="space-y-3 pt-4">
                          <input type="text" placeholder="User UID" value={debugUserId} onChange={(e) => setDebugUserId(e.target.value)} className="w-full px-5 py-3.5 bg-gray-50 dark:bg-black/50 rounded-xl font-mono text-xs outline-none border border-gray-100 dark:border-white/10 text-black dark:text-white" />
                          <input type="password" placeholder="Test Password" value={debugPassword} onChange={(e) => setDebugPassword(e.target.value)} className="w-full px-5 py-3.5 bg-gray-50 dark:bg-black/50 rounded-xl font-bold text-sm outline-none border border-gray-100 dark:border-white/10 text-black dark:text-white" />
                          <Button onClick={handleCheckPassword} disabled={isDebugLoading} className="w-full py-3.5 rounded-xl font-black bg-blue-600 text-white hover:bg-blue-700 text-xs">{isDebugLoading ? 'Verifying...' : 'Check Match'}</Button>
                        </div>
                      </div>

                      {/* Deriv Universal Unlinker */}
                      <div className="bg-white dark:bg-white/5 p-6 sm:p-8 rounded-[2rem] sm:rounded-[2.5rem] border border-gray-100 dark:border-white/10 space-y-6 flex flex-col">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-red-500/10 rounded-xl text-red-500"><HugeiconsIcon icon={Delete02Icon} size={20} /></div>
                          <h3 className="font-black text-sm uppercase tracking-tight">Deriv Unlinker</h3>
                        </div>
                        <p className="text-[10px] text-muted-gray leading-relaxed uppercase font-bold flex-1">Disconnect any user from Deriv by their UID.</p>
                        <div className="space-y-3 pt-4">
                          <input type="text" placeholder="User UID" value={unlinkUserId} onChange={(e) => setUnlinkUserId(e.target.value)} className="w-full px-5 py-3.5 bg-gray-50 dark:bg-black/50 rounded-xl font-mono text-xs outline-none border border-gray-100 dark:border-white/10 text-black dark:text-white" />
                          <Button onClick={handleUnlinkUniversal} disabled={isUnlinkingGeneral} variant="danger" className="w-full py-3.5 rounded-xl font-black text-xs mt-4">{isUnlinkingGeneral ? 'Clearing...' : 'Unlink Connection'}</Button>
                        </div>
                      </div>

                      {/* Force-Complete Tx */}
                      <div className="bg-white dark:bg-white/5 p-6 sm:p-8 rounded-[2rem] sm:rounded-[2.5rem] border border-gray-100 dark:border-white/10 space-y-6 flex flex-col">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-orange-500/10 rounded-xl text-orange-500"><HugeiconsIcon icon={FlashIcon} size={20} /></div>
                          <h3 className="font-black text-sm uppercase tracking-tight">Force-Complete Tx</h3>
                        </div>
                        <p className="text-[10px] text-muted-gray leading-relaxed uppercase font-bold flex-1">Bypass Deriv and manually mark a transaction as completed to trigger rewards.</p>
                        <div className="space-y-3 pt-4">
                          <input type="text" placeholder="Transaction UID" value={forceTxUuid} onChange={(e) => setForceTxUuid(e.target.value)} className="w-full px-5 py-3.5 bg-gray-50 dark:bg-black/50 rounded-xl font-mono text-xs outline-none border border-gray-100 dark:border-white/10 text-black dark:text-white" />
                          <Button onClick={handleForceComplete} disabled={isForceLoading} variant="secondary" className="w-full py-3.5 rounded-xl font-black text-xs bg-orange-500 text-white hover:bg-orange-600 border-none mt-4">{isForceLoading ? 'Processing...' : 'Force Complete & Reward'}</Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'settings' && (
                  <div className="p-10 max-w-lg mx-auto text-center space-y-10">
                    <div className="bg-soft-green/10 p-6 rounded-full inline-block"><HugeiconsIcon icon={Settings01Icon} size={48} className="text-soft-green" /></div>
                    
                    <div>
                      <h2 className="text-2xl font-black">System Settings</h2>
                      <p className="text-gray-500 text-sm mt-2">Manage critical platform configurations.</p>
                    </div>

                    <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-white/10 text-left">
                      <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest pl-2 mb-2">Verification Payment Account</p>
                      
                      <div className="bg-black/5 dark:bg-white/5 p-8 rounded-[2rem] border border-gray-100 dark:border-white/10 flex flex-col items-center justify-center text-center mb-6 relative group">
                        <button 
                          onClick={async () => {
                            setIsRefreshing(true);
                            await fetchData(true);
                            setTimeout(() => setIsRefreshing(false), 600);
                          }}
                          disabled={isRefreshing}
                          className={`absolute top-4 right-4 p-2 text-muted-gray hover:text-soft-green transition-all ${isRefreshing ? 'animate-spin text-soft-green' : ''}`}
                          title="Refresh Account Data"
                        >
                          <HugeiconsIcon icon={Refresh01Icon} size={16} />
                        </button>
                        <p className="text-[10px] font-bold text-muted-gray uppercase tracking-widest mb-3">Current Active CR</p>
                        <p className="text-5xl font-black text-soft-green font-mono tracking-tighter">
                          {displayAccount.deriv_cr_number || 'NOT SET'}
                        </p>
                        <p className="text-[10px] font-black text-muted-gray uppercase tracking-widest mt-4">Current Fee: ${parseFloat(displayAccount.verification_fee || 0).toFixed(2)}</p>
                      </div>

                      <div className="space-y-4">
                        <input type="text" placeholder="Update Admin CR Number" value={adminAccount.deriv_cr_number} onChange={(e) => setAdminAccount({...adminAccount, deriv_cr_number: e.target.value})} className="w-full px-6 py-4 bg-gray-50 dark:bg-black/50 rounded-2xl font-bold outline-none border border-gray-100 dark:border-white/10 text-black dark:text-white" />
                        <input type="number" placeholder="Update Verification Fee (USD)" value={adminAccount.verification_fee} onChange={(e) => setAdminAccount({...adminAccount, verification_fee: e.target.value})} className="w-full px-6 py-4 bg-gray-50 dark:bg-black/50 rounded-2xl font-bold outline-none border border-gray-100 dark:border-white/10 text-black dark:text-white" />
                        <Button onClick={handleUpdateAccount} disabled={isUpdatingAccount} className="w-full py-4 rounded-2xl font-black bg-black text-white hover:bg-gray-800 dark:bg-white dark:text-black">
                          {isUpdatingAccount ? 'Updating...' : 'Update Main Account'}
                        </Button>
                      </div>
                    </div>

                    <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-white/10 text-left">
                      <div className="flex items-center justify-between px-2 mb-2">
                        <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest">Team Oversight</p>
                        <span className="text-[10px] font-black bg-soft-green/10 text-soft-green px-2 py-0.5 rounded-lg">{admins.length} Admins</span>
                      </div>
                      
                      <div className="space-y-2">
                        {admins.map(a => (
                          <div key={a.uid} className="flex items-center justify-between p-4 bg-white dark:bg-black/20 border border-gray-100 dark:border-white/5 rounded-2xl group">
                            <div className="min-w-0">
                              <p className="font-bold text-sm truncate">{a.email}</p>
                              {a.email === user.email && <span className="text-[8px] font-black uppercase text-soft-green tracking-tighter">You</span>}
                            </div>
                            {a.email !== user.email && (
                              <button 
                                onClick={() => handleRevokeAdmin(a.email)}
                                className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-all"
                                title="Revoke Access"
                              >
                                <HugeiconsIcon icon={Delete02Icon} size={18} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-white/10 text-left">
                      <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest pl-2">Dashboard Refresh Rate</p>
                      <div className="flex justify-center gap-3 pt-2">
                        {[10000, 5000, 2000, 1500].map(ms => (
                          <button key={ms} onClick={() => updateRefreshInterval(ms)} className={`px-4 py-3 rounded-xl font-black text-xs transition-all border ${refreshInterval === ms ? 'bg-black text-white dark:bg-white dark:text-black border-transparent shadow-lg scale-105' : 'bg-transparent text-gray-500 border-gray-200 dark:border-white/10 hover:border-black dark:hover:border-white'}`}>{ms/1000}s</button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-4 pt-4 border-t border-gray-100 dark:border-white/10 text-left">
                      <p className="text-[10px] font-black uppercase text-gray-400 tracking-widest pl-2">Admin Security Passcode</p>
                      <input type="text" placeholder="New Terminal Key" value={newAdminPass} onChange={(e) => setNewAdminPass(e.target.value)} className="w-full px-6 py-4 bg-gray-50 dark:bg-black/50 rounded-2xl text-center font-bold outline-none border border-gray-100 dark:border-white/10 text-black dark:text-white" />
                      <Button onClick={handleUpdatePassword} className="w-full py-4 rounded-2xl font-black bg-red-50 text-red-500 border-none hover:bg-red-100">Update Key</Button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <ResponsiveModal isOpen={!!metricDetail} onClose={() => setMetricDetail(null)} title="Insights">
        <div className="py-6 space-y-6 text-black">
          {metricDetail === 'users' && metrics && (
            <div className="space-y-4">
              <div className="flex justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl"><span className="font-bold text-sm">Total Clients</span><span className="font-black">{metrics.users.total_clients}</span></div>
              <div className="flex justify-between p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl"><span className="font-bold text-sm">Total Agents</span><span className="font-black">{metrics.users.total_agents}</span></div>
              <div className="flex justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-xl"><span className="font-bold text-sm">Verified Agents</span><span className="font-black">{metrics.users.total_verified_agents}</span></div>
            </div>
          )}
          {metricDetail === 'transactions' && metrics && (
            <div className="space-y-4">
              <div className="flex justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-xl"><span className="font-bold text-sm">Completed</span><span className="font-black text-green-600">{metrics.transactions.total_completed}</span></div>
              <div className="flex justify-between p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-xl"><span className="font-bold text-sm">Pending</span><span className="font-black text-yellow-600">{metrics.transactions.total_pending}</span></div>
              <div className="flex justify-between p-4 bg-red-50 dark:bg-red-900/20 rounded-xl"><span className="font-bold text-sm">Rejected</span><span className="font-black text-red-600">{metrics.transactions.total_rejected}</span></div>
            </div>
          )}
          {metricDetail === 'volume' && metrics && (
            <div className="space-y-4">
              <div className="p-6 bg-purple-50 dark:bg-purple-900/20 rounded-2xl text-center"><p className="text-xs uppercase font-black text-purple-600 mb-1">USD Volume</p><p className="text-3xl font-black">${parseFloat(metrics.volume.total_usd).toLocaleString()}</p></div>
              <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded-2xl text-center"><p className="text-xs uppercase font-black text-gray-500 mb-1">NGN Volume</p><p className="text-3xl font-black">₦{parseFloat(metrics.volume.total_ngn).toLocaleString()}</p></div>
            </div>
          )}
          <Button onClick={() => setMetricDetail(null)} className="w-full py-4 rounded-xl font-black">Close</Button>
        </div>
      </ResponsiveModal>

      <ResponsiveModal isOpen={isAdminInviteOpen} onClose={() => setIsAdminInviteOpen(false)} title="Promote to Administrator">
        <div className="py-6 space-y-6">
          <input type="email" placeholder="Email Address" value={adminEmail} onChange={(e) => setAdminInviteEmail(e.target.value)} className="w-full px-6 py-4 bg-gray-50 dark:bg-gray-800 rounded-xl font-bold text-black dark:text-white border border-gray-100 dark:border-white/10 outline-none focus:ring-2 focus:ring-soft-green" />
          <Button onClick={handleInviteAdmin} className="w-full py-4 rounded-xl font-black">Promote</Button>
        </div>
      </ResponsiveModal>

      <ResponsiveModal isOpen={isMoreMenuOpen} onClose={() => setIsMoreMenuOpen(false)} title="Administration Menu">
        <div className="py-6 grid grid-cols-2 gap-4">
          {[
            { id: 'verifications', label: 'Verifications', icon: Clock01Icon, color: 'text-soft-green', bg: 'bg-soft-green/10' },
            { id: 'tools', label: 'Toolkit', icon: Settings01Icon, color: 'text-purple-500', bg: 'bg-purple-500/10' },
            { id: 'settings', label: 'System Settings', icon: LockKeyIcon, color: 'text-gray-500', bg: 'bg-gray-500/10' }
          ].map(item => (
            <button 
              key={item.id} 
              onClick={() => { setActiveTab(item.id); setIsMoreMenuOpen(false); }}
              className={`flex flex-col items-center gap-3 p-6 rounded-[2rem] border transition-all ${activeTab === item.id ? 'border-soft-green bg-soft-green/5' : 'border-gray-100 dark:border-white/5 bg-white dark:bg-black/20'}`}
            >
              <div className={`p-3 rounded-2xl ${item.bg} ${item.color}`}><HugeiconsIcon icon={item.icon} size={24} /></div>
              <span className="text-[10px] font-black uppercase tracking-widest text-black dark:text-white">{item.label}</span>
            </button>
          ))}
          <Button 
            variant="danger" 
            onClick={() => { setIsGateUnlocked(false); sessionStorage.removeItem('admin_gate_unlocked'); setIsMoreMenuOpen(false); }}
            className="col-span-2 py-4 rounded-2xl font-black text-xs mt-4 flex items-center justify-center gap-2"
          >
            <HugeiconsIcon icon={LockKeyIcon} size={16} />
            <span>Lock Terminal Session</span>
          </Button>
        </div>
      </ResponsiveModal>

      <ResponsiveModal isOpen={isRefundModalOpen} onClose={() => setIsRefundModalOpen(false)} title="Agent Verification Hub">
        <div className="py-6 space-y-8 text-black dark:text-white">
          <div className="text-center">
            <p className="text-[10px] font-black uppercase text-muted-gray tracking-widest mb-1">Target Agent</p>
            <p className="font-bold text-lg">{selectedVerif?.user?.email}</p>
          </div>

          <div className="bg-soft-green/5 p-6 rounded-[2rem] border border-soft-green/10 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-soft-green/20 rounded-lg text-soft-green"><HugeiconsIcon icon={CheckmarkCircle01Icon} size={18} /></div>
              <h3 className="font-black text-sm uppercase tracking-tight">Option A: Automated Refund</h3>
            </div>
            
            {!showRefundOtpInput ? (
              <>
                <p className="text-xs text-muted-gray leading-relaxed">
                  Safzi will securely initiate a withdrawal from your account to <b>{selectedVerif?.user?.deriv_accounts?.[0]?.deriv_id}</b>.
                </p>
                <Button onClick={handleRequestRefundOTP} disabled={loading} className="w-full py-4 rounded-xl font-black shadow-lg">
                  {loading ? 'Requesting...' : 'Request Refund OTP'}
                </Button>
              </>
            ) : (
              <div className="space-y-4 animate-in slide-in-from-top-2 duration-300">
                <div>
                  <label className="block text-[10px] font-black uppercase text-soft-green mb-2 px-1">Enter Admin OTP</label>
                  <input 
                    type="text" 
                    placeholder="TOKEN" 
                    value={refundOtp} 
                    onChange={(e) => setRefundOtp(e.target.value)} 
                    className="w-full px-5 py-4 bg-white dark:bg-black/20 rounded-2xl font-mono text-xl text-center outline-none border border-soft-green/30 text-black dark:text-white"
                  />
                </div>
                <Button onClick={() => handleProcessRefund(true)} disabled={loading || !refundOtp} className="w-full py-4 rounded-xl font-black shadow-lg">
                  {loading ? 'Verifying...' : 'Verify & Execute Refund'}
                </Button>
                <button onClick={() => setShowRefundOtpInput(false)} className="w-full text-[10px] font-black uppercase text-gray-400 hover:text-gray-600 transition-colors">Cancel OTP</button>
              </div>
            )}
          </div>

          <div className="relative flex items-center px-4">
            <div className="flex-grow border-t border-gray-100 dark:border-white/10"></div>
            <span className="flex-shrink mx-4 text-[9px] font-black uppercase text-gray-400 italic">Or</span>
            <div className="flex-grow border-t border-gray-100 dark:border-white/10"></div>
          </div>

          <div className="space-y-4 px-2">
            <div className="flex items-center gap-2 mb-2">
              <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-muted-gray"><HugeiconsIcon icon={Clock01Icon} size={18} /></div>
              <h3 className="font-black text-sm uppercase tracking-tight text-muted-gray">Option B: Manual Verification</h3>
            </div>
            <p className="text-xs text-muted-gray leading-relaxed">
              Use this if you already sent the funds manually on Deriv. Paste the <b>Transaction ID</b> below to verify the agent.
            </p>
            
            <div className="space-y-2">
              <div className="flex items-center justify-between bg-gray-50 dark:bg-black/20 p-3 rounded-xl border border-gray-100 dark:border-white/5 mb-2">
                <span className="font-mono text-sm font-bold">{selectedVerif?.user?.deriv_accounts?.[0]?.deriv_id}</span>
                <button onClick={() => { navigator.clipboard.writeText(selectedVerif?.user?.deriv_accounts?.[0]?.deriv_id || ''); addToast("CR Number copied!", "success"); }} className="text-soft-green hover:scale-110 transition-transform">
                  <HugeiconsIcon icon={Copy01Icon} size={18} />
                </button>
              </div>
              <input type="text" placeholder="Paste Deriv Transaction ID" value={refundTxId} onChange={(e) => setRefundTxId(e.target.value)} className="w-full px-5 py-4 bg-gray-50 dark:bg-gray-800 rounded-2xl font-mono text-sm text-black dark:text-white outline-none focus:ring-2 focus:ring-soft-green border-none" />
            </div>

            <Button onClick={() => handleProcessRefund(false)} disabled={loading || !refundTxId} variant="secondary" className="w-full py-4 rounded-xl font-black">
              Verify Manually
            </Button>
          </div>
        </div>
      </ResponsiveModal>
    </>
  );
};

export default Admin;
